import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, 
  BarChart3, PieChart, Calendar, ArrowUpRight, ArrowDownRight,
  Newspaper, Share2, Printer, Package, CreditCard, Users,
  RefreshCw, Download, FileSpreadsheet, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RePieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  digital_pr: '#D4AF37',
  social_media: '#8B5CF6',
  print_media: '#F59E0B',
  bundles: '#10B981'
};

const SERVICE_LABELS = {
  digital_pr: 'Digital PR',
  social_media: 'Social Media',
  print_media: 'Print Media',
  bundles: 'Bundles'
};

const formatFullCurrency = (value) => {
  return `₹${value.toLocaleString('en-IN')}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1a1a1a] border border-[#D4AF37]/30 p-3 rounded-lg shadow-lg">
        <p className="font-manrope text-sm text-white font-semibold mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="font-manrope text-xs" style={{ color: entry.color }}>
            {entry.name}: {formatFullCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const AdminRevenue = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30days');
  const [overview, setOverview] = useState(null);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [topServices, setTopServices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [chartView, setChartView] = useState('daily'); // daily, monthly

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const [overviewRes, dailyRes, monthlyRes, topRes, paymentRes] = await Promise.all([
          axios.get(`${API}/admin/revenue/overview?period=${period}`, { headers }),
          axios.get(`${API}/admin/revenue/daily-trend?days=${period === '7days' ? 7 : period === '90days' ? 90 : 30}`, { headers }),
          axios.get(`${API}/admin/revenue/monthly-trend?months=12`, { headers }),
          axios.get(`${API}/admin/revenue/top-services?period=${period}`, { headers }),
          axios.get(`${API}/admin/revenue/payment-methods?period=${period}`, { headers })
        ]);

        setOverview(overviewRes.data);
        setDailyTrend(dailyRes.data.trend || []);
        setMonthlyTrend(monthlyRes.data.trend || []);
        setTopServices(topRes.data.top_services || []);
        setPaymentMethods(paymentRes.data);
      } catch (error) {
        console.error('Error fetching revenue data:', error);
        toast.error('Failed to load revenue data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [overviewRes, dailyRes, monthlyRes, topRes, paymentRes] = await Promise.all([
        axios.get(`${API}/admin/revenue/overview?period=${period}`, { headers }),
        axios.get(`${API}/admin/revenue/daily-trend?days=${period === '7days' ? 7 : period === '90days' ? 90 : 30}`, { headers }),
        axios.get(`${API}/admin/revenue/monthly-trend?months=12`, { headers }),
        axios.get(`${API}/admin/revenue/top-services?period=${period}`, { headers }),
        axios.get(`${API}/admin/revenue/payment-methods?period=${period}`, { headers })
      ]);

      setOverview(overviewRes.data);
      setDailyTrend(dailyRes.data.trend || []);
      setMonthlyTrend(monthlyRes.data.trend || []);
      setTopServices(topRes.data.top_services || []);
      setPaymentMethods(paymentRes.data);
    } catch (error) {
      console.error('Error fetching revenue data:', error);
      toast.error('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value.toLocaleString()}`;
  };

  // Prepare pie chart data
  const pieData = overview ? Object.entries(overview.revenue_by_type)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => ({
      name: SERVICE_LABELS[key],
      value: value,
      color: COLORS[key]
    })) : [];

  if (loading && !overview) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-[#D4AF37] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="revenue-dashboard">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-playfair text-2xl font-bold text-[#1a1a1a]">Revenue Dashboard</h1>
            <p className="font-manrope text-sm text-[#737373]">Track your revenue and sales performance</p>
          </div>
          
          <div className="flex items-center gap-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid="period-select"
              className="px-4 py-2 border border-[#e5e5e5] rounded-lg font-manrope text-sm focus:outline-none focus:border-[#D4AF37]"
            >
              <option value="today">Today</option>
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="90days">Last 90 Days</option>
              <option value="this_month">This Month</option>
              <option value="this_year">This Year</option>
              <option value="all">All Time</option>
            </select>
            
            <button
              onClick={fetchAllData}
              className="p-2 border border-[#e5e5e5] rounded-lg hover:bg-[#fafafa] transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-5 h-5 text-[#737373] ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Revenue */}
          <div className="bg-gradient-to-br from-[#D4AF37] to-[#B8960F] p-6 rounded-xl text-white" data-testid="total-revenue-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
              {overview?.revenue_growth !== 0 && (
                <div className={`flex items-center gap-1 text-sm ${overview?.revenue_growth >= 0 ? 'text-green-200' : 'text-red-200'}`}>
                  {overview?.revenue_growth >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {Math.abs(overview?.revenue_growth || 0)}%
                </div>
              )}
            </div>
            <p className="font-manrope text-sm text-white/80 mb-1">Total Revenue</p>
            <p className="font-playfair text-3xl font-bold">{formatCurrency(overview?.total_revenue || 0)}</p>
          </div>

          {/* Total Orders */}
          <div className="bg-white border border-[#e5e5e5] p-6 rounded-xl" data-testid="total-orders-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <p className="font-manrope text-sm text-[#737373] mb-1">Total Orders</p>
            <p className="font-playfair text-3xl font-bold text-[#1a1a1a]">{overview?.total_orders || 0}</p>
          </div>

          {/* Avg Order Value */}
          <div className="bg-white border border-[#e5e5e5] p-6 rounded-xl" data-testid="avg-order-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <p className="font-manrope text-sm text-[#737373] mb-1">Avg Order Value</p>
            <p className="font-playfair text-3xl font-bold text-[#1a1a1a]">{formatCurrency(overview?.avg_order_value || 0)}</p>
          </div>

          {/* Partner Revenue */}
          <div className="bg-white border border-[#e5e5e5] p-6 rounded-xl" data-testid="partner-revenue-card">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="font-manrope text-sm text-[#737373] mb-1">Partner Revenue</p>
            <p className="font-playfair text-3xl font-bold text-[#1a1a1a]">{formatCurrency(overview?.partner_revenue || 0)}</p>
            <p className="font-manrope text-xs text-[#737373] mt-1">{overview?.partner_orders_count || 0} orders</p>
          </div>
        </div>

        {/* Revenue by Service Type */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Object.entries(overview?.revenue_by_type || {}).map(([key, value]) => {
            const orders = overview?.orders_by_type?.[key] || 0;
            const icons = {
              digital_pr: Newspaper,
              social_media: Share2,
              print_media: Printer,
              bundles: Package
            };
            const Icon = icons[key];
            
            return (
              <div 
                key={key} 
                className="bg-white border border-[#e5e5e5] p-4 rounded-xl hover:border-[#D4AF37] transition-colors"
                data-testid={`service-revenue-${key}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${COLORS[key]}20` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: COLORS[key] }} />
                  </div>
                  <span className="font-manrope text-sm font-medium text-[#1a1a1a]">{SERVICE_LABELS[key]}</span>
                </div>
                <p className="font-playfair text-xl font-bold text-[#1a1a1a]">{formatCurrency(value)}</p>
                <p className="font-manrope text-xs text-[#737373]">{orders} orders</p>
              </div>
            );
          })}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Revenue Trend Chart */}
          <div className="lg:col-span-2 bg-white border border-[#e5e5e5] rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-playfair text-lg font-bold text-[#1a1a1a]">Revenue Trend</h2>
              <div className="flex border border-[#e5e5e5] rounded-lg overflow-hidden">
                <button
                  onClick={() => setChartView('daily')}
                  className={`px-4 py-2 font-manrope text-sm ${chartView === 'daily' ? 'bg-[#D4AF37] text-white' : 'text-[#737373] hover:bg-[#fafafa]'}`}
                >
                  Daily
                </button>
                <button
                  onClick={() => setChartView('monthly')}
                  className={`px-4 py-2 font-manrope text-sm ${chartView === 'monthly' ? 'bg-[#D4AF37] text-white' : 'text-[#737373] hover:bg-[#fafafa]'}`}
                >
                  Monthly
                </button>
              </div>
            </div>
            
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                {chartView === 'daily' ? (
                  <AreaChart data={dailyTrend}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      tick={{ fontSize: 12, fill: '#737373' }}
                    />
                    <YAxis 
                      tickFormatter={formatCurrency}
                      tick={{ fontSize: 12, fill: '#737373' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#D4AF37" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorRevenue)" 
                      name="Revenue"
                    />
                  </AreaChart>
                ) : (
                  <BarChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fontSize: 12, fill: '#737373' }}
                    />
                    <YAxis 
                      tickFormatter={formatCurrency}
                      tick={{ fontSize: 12, fill: '#737373' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="digital_pr" name="Digital PR" fill={COLORS.digital_pr} stackId="stack" />
                    <Bar dataKey="social_media" name="Social Media" fill={COLORS.social_media} stackId="stack" />
                    <Bar dataKey="print_media" name="Print Media" fill={COLORS.print_media} stackId="stack" />
                    <Bar dataKey="bundles" name="Bundles" fill={COLORS.bundles} stackId="stack" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>

          {/* Revenue Distribution Pie Chart */}
          <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
            <h2 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-6">Revenue Distribution</h2>
            
            {pieData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatFullCurrency(value)}
                      contentStyle={{ backgroundColor: '#1a1a1a', border: 'none', borderRadius: '8px' }}
                      itemStyle={{ color: '#fff' }}
                    />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-[#737373]">
                No data available
              </div>
            )}
            
            {/* Legend */}
            <div className="space-y-2 mt-4">
              {pieData.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="font-manrope text-sm text-[#737373]">{item.name}</span>
                  </div>
                  <span className="font-manrope text-sm font-medium text-[#1a1a1a]">{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Services & Payment Methods */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Services */}
          <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
            <h2 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-4">Top Performing Services</h2>
            
            <div className="space-y-3">
              {topServices.length > 0 ? (
                topServices.slice(0, 5).map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#fafafa] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[#D4AF37] text-white rounded-full flex items-center justify-center font-manrope text-xs font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-manrope text-sm font-medium text-[#1a1a1a] line-clamp-1">{service.name}</p>
                        <p className="font-manrope text-xs text-[#737373]">{service.type} • {service.orders} orders</p>
                      </div>
                    </div>
                    <span className="font-manrope text-sm font-bold text-[#1a1a1a]">{formatCurrency(service.revenue)}</span>
                  </div>
                ))
              ) : (
                <p className="text-center text-[#737373] py-8">No data available</p>
              )}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
            <h2 className="font-playfair text-lg font-bold text-[#1a1a1a] mb-4">Payment Methods</h2>
            
            {paymentMethods && (
              <div className="space-y-4">
                {/* Razorpay */}
                <div className="p-4 bg-[#fafafa] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <span className="font-manrope text-sm font-medium text-[#1a1a1a]">Razorpay</span>
                    </div>
                    <span className="font-manrope text-sm font-bold text-[#1a1a1a]">
                      {formatCurrency(paymentMethods.razorpay?.revenue || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-[#737373]">
                    <span>{paymentMethods.razorpay?.orders || 0} orders</span>
                    <span>
                      {paymentMethods.total?.revenue > 0 
                        ? Math.round((paymentMethods.razorpay?.revenue / paymentMethods.total.revenue) * 100) 
                        : 0}% of total
                    </span>
                  </div>
                </div>

                {/* Partner Credits */}
                <div className="p-4 bg-[#fafafa] rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Users className="w-5 h-5 text-green-600" />
                      <span className="font-manrope text-sm font-medium text-[#1a1a1a]">Partner Credits</span>
                    </div>
                    <span className="font-manrope text-sm font-bold text-[#1a1a1a]">
                      {formatCurrency(paymentMethods.partner_credits?.revenue || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-[#737373]">
                    <span>{paymentMethods.partner_credits?.orders || 0} orders</span>
                    <span>
                      {paymentMethods.total?.revenue > 0 
                        ? Math.round((paymentMethods.partner_credits?.revenue / paymentMethods.total.revenue) * 100) 
                        : 0}% of total
                    </span>
                  </div>
                </div>

                {/* Discounts Given */}
                <div className="p-4 border border-[#e5e5e5] rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-manrope text-sm text-[#737373]">Total Discounts Given</span>
                    <span className="font-manrope text-sm font-bold text-red-500">
                      -{formatCurrency(overview?.total_discounts_given || 0)}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* GST Report Section */}
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-6 mt-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-playfair text-xl font-bold text-[#1a1a1a] flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-[#D4AF37]" />
                Monthly GST Report
              </h2>
              <p className="font-manrope text-sm text-[#737373] mt-1">
                Download GST reports for filing
              </p>
            </div>
          </div>
          
          <GSTReportDownloader />
        </div>
      </div>
    </AdminLayout>
  );
};

// GST Report Downloader Component
const GSTReportDownloader = () => {
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  
  const years = [2024, 2025, 2026].filter(y => y <= currentDate.getFullYear());
  
  const fetchReport = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/revenue/gst-report?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch GST report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  };
  
  const downloadCSV = () => {
    const token = localStorage.getItem('token');
    window.open(
      `${API}/admin/revenue/gst-report/download?month=${selectedMonth}&year=${selectedYear}&token=${token}`,
      '_blank'
    );
  };
  
  const handleDownloadCSV = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(
        `${API}/admin/revenue/gst-report/download?month=${selectedMonth}&year=${selectedYear}`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gst_report_${selectedMonth}_${selectedYear}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('GST Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };
  
  return (
    <div>
      <div className="flex flex-wrap items-end gap-4 mb-6">
        <div>
          <label className="block font-manrope text-xs text-[#737373] mb-2 uppercase tracking-widest">Month</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(Number(e.target.value))}
            className="border border-[#e5e5e5] rounded-lg px-4 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none bg-white min-w-[150px]"
          >
            {months.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block font-manrope text-xs text-[#737373] mb-2 uppercase tracking-widest">Year</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-[#e5e5e5] rounded-lg px-4 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none bg-white min-w-[100px]"
          >
            {years.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        
        <button
          onClick={fetchReport}
          disabled={loading}
          className="px-6 py-2 bg-[#1a1a1a] text-white rounded-lg font-manrope text-sm hover:bg-[#333] transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
          View Report
        </button>
        
        {report && report.invoices?.length > 0 && (
          <button
            onClick={handleDownloadCSV}
            className="px-6 py-2 bg-[#D4AF37] text-[#0d0d0d] rounded-lg font-manrope text-sm hover:bg-[#B8960F] transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download CSV
          </button>
        )}
      </div>
      
      {/* Report Display */}
      {report && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-[#f5f5f5] rounded-lg p-4">
              <p className="font-manrope text-xs text-[#737373] uppercase tracking-widest mb-1">Total Invoices</p>
              <p className="font-playfair text-2xl font-bold text-[#1a1a1a]">{report.summary.total_invoices}</p>
            </div>
            <div className="bg-[#f5f5f5] rounded-lg p-4">
              <p className="font-manrope text-xs text-[#737373] uppercase tracking-widest mb-1">Taxable Amount</p>
              <p className="font-playfair text-2xl font-bold text-[#1a1a1a]">₹{report.summary.total_base_amount.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-[#D4AF37]/10 rounded-lg p-4">
              <p className="font-manrope text-xs text-[#737373] uppercase tracking-widest mb-1">Total GST</p>
              <p className="font-playfair text-2xl font-bold text-[#D4AF37]">₹{report.summary.total_gst.toLocaleString('en-IN')}</p>
            </div>
            <div className="bg-[#1a1a1a] rounded-lg p-4">
              <p className="font-manrope text-xs text-gray-400 uppercase tracking-widest mb-1">Total Revenue</p>
              <p className="font-playfair text-2xl font-bold text-white">₹{report.summary.total_amount.toLocaleString('en-IN')}</p>
            </div>
          </div>
          
          {/* GST Rate Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-[#e5e5e5] rounded-lg p-4">
              <h4 className="font-manrope text-sm font-semibold text-[#1a1a1a] mb-3">GST @ 18% (CGST 9% + SGST 9%)</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#737373]">Taxable Amount:</span>
                  <span className="font-mono">₹{report.by_rate.gst_18.taxable_amount.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#737373]">CGST @ 9%:</span>
                  <span className="font-mono">₹{report.by_rate.gst_18.cgst_9.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#737373]">SGST @ 9%:</span>
                  <span className="font-mono">₹{report.by_rate.gst_18.sgst_9.toLocaleString('en-IN')}</span>
                </div>
                <hr className="border-[#e5e5e5]" />
                <div className="flex justify-between font-semibold">
                  <span>Total Tax:</span>
                  <span className="font-mono text-[#D4AF37]">₹{report.by_rate.gst_18.total_tax.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
            
            <div className="border border-[#e5e5e5] rounded-lg p-4">
              <h4 className="font-manrope text-sm font-semibold text-[#1a1a1a] mb-3">GST @ 5% (CGST 2.5% + SGST 2.5%)</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#737373]">Taxable Amount:</span>
                  <span className="font-mono">₹{report.by_rate.gst_5.taxable_amount.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#737373]">CGST @ 2.5%:</span>
                  <span className="font-mono">₹{report.by_rate.gst_5.cgst_2_5.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#737373]">SGST @ 2.5%:</span>
                  <span className="font-mono">₹{report.by_rate.gst_5.sgst_2_5.toLocaleString('en-IN')}</span>
                </div>
                <hr className="border-[#e5e5e5]" />
                <div className="flex justify-between font-semibold">
                  <span>Total Tax:</span>
                  <span className="font-mono text-[#D4AF37]">₹{report.by_rate.gst_5.total_tax.toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Invoice Table */}
          {report.invoices?.length > 0 ? (
            <div className="border border-[#e5e5e5] rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#f5f5f5]">
                    <tr>
                      <th className="px-4 py-3 text-left font-manrope text-xs text-[#737373] uppercase tracking-widest">Invoice</th>
                      <th className="px-4 py-3 text-left font-manrope text-xs text-[#737373] uppercase tracking-widest">Date</th>
                      <th className="px-4 py-3 text-left font-manrope text-xs text-[#737373] uppercase tracking-widest">Customer</th>
                      <th className="px-4 py-3 text-left font-manrope text-xs text-[#737373] uppercase tracking-widest">GSTIN</th>
                      <th className="px-4 py-3 text-right font-manrope text-xs text-[#737373] uppercase tracking-widest">Taxable</th>
                      <th className="px-4 py-3 text-right font-manrope text-xs text-[#737373] uppercase tracking-widest">GST</th>
                      <th className="px-4 py-3 text-right font-manrope text-xs text-[#737373] uppercase tracking-widest">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.invoices.map((inv, idx) => (
                      <tr key={idx} className="border-t border-[#e5e5e5] hover:bg-[#fafafa]">
                        <td className="px-4 py-3 font-mono text-sm">{inv.invoice_no}</td>
                        <td className="px-4 py-3 font-manrope text-sm text-[#737373]">{inv.date}</td>
                        <td className="px-4 py-3 font-manrope text-sm">{inv.customer_name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#737373]">{inv.customer_gstin || '-'}</td>
                        <td className="px-4 py-3 font-mono text-sm text-right">₹{inv.base_amount.toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 font-mono text-sm text-right text-[#D4AF37]">
                          ₹{(inv.cgst + inv.sgst).toLocaleString('en-IN')}
                          <span className="text-xs text-[#737373] ml-1">({inv.gst_rate}%)</span>
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-right font-semibold">₹{inv.total_amount.toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-[#f5f5f5] rounded-lg">
              <FileSpreadsheet className="w-12 h-12 mx-auto text-[#e5e5e5] mb-3" />
              <p className="font-manrope text-[#737373]">No invoices found for {report.period}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminRevenue;
